/** Small accessory skeletons for secondary hair motion. Body/face bones and cached meshes stay immutable. */
import * as THREE from 'three';
import type {HairRigDef} from './hairRig';

export interface HairCollision { a:THREE.Vector3; b:THREE.Vector3; radius:number }
const clamp=THREE.MathUtils.clamp;
function closest(p:THREE.Vector3,a:THREE.Vector3,b:THREE.Vector3,out:THREE.Vector3):number{
 out.copy(b).sub(a);const t=clamp(((p.x-a.x)*out.x+(p.y-a.y)*out.y+(p.z-a.z)*out.z)/Math.max(1e-10,out.lengthSq()),0,1);out.multiplyScalar(t).add(a);return out.distanceTo(p);
}
interface Particle { p:THREE.Vector3; v:THREE.Vector3; goal:THREE.Vector3; previousGoal:THREE.Vector3; pinned:boolean; radii:number[] }
/** Frame-space, substepped spring/constraint solver. A stationary co-rotating frame produces no phantom sway. */
export class HairSolver {
 readonly nodes:Particle[][];
 private initialized=false;
 constructor(readonly def:HairRigDef){this.nodes=def.chains.map(ch=>ch.points.map((p,i)=>({p:new THREE.Vector3(...p),v:new THREE.Vector3(),goal:new THREE.Vector3(...p),previousGoal:new THREE.Vector3(...p),pinned:i<ch.pinned,radii:[]})));}
 reset():void{this.initialized=false;}
 step(dt:number,goals:THREE.Vector3[][],caps:HairCollision[],wind:THREE.Vector3,down:THREE.Vector3,water=0):void{
  const jump=this.initialized&&goals.some((ch,i)=>ch[0].distanceTo(this.nodes[i][0].previousGoal)>.5);
  if(!this.initialized||jump||dt>.15||dt<=0){
   const q=new THREE.Vector3();this.nodes.forEach((ch,i)=>ch.forEach((n,j)=>{n.p.copy(goals[i][j]);n.goal.copy(n.p);n.previousGoal.copy(n.p);n.v.set(0,0,0);n.radii=caps.map(c=>Math.max(0,Math.min(c.radius,closest(n.p,c.a,c.b,q)-.003)));}));
   this.initialized=true;return;
  }
  const elapsed=Math.min(dt,.06),steps=Math.max(1,Math.ceil(elapsed*120)),h=elapsed/steps;
  const delta=new THREE.Vector3(),targetVelocity=new THREE.Vector3(),q=new THREE.Vector3();
  for(let step=1;step<=steps;step++){
   this.nodes.forEach((ch,i)=>ch.forEach((n,j)=>{
    n.goal.lerpVectors(n.previousGoal,goals[i][j],step/steps);
    if(n.pinned){n.p.copy(n.goal);n.v.set(0,0,0);return;}
    targetVelocity.copy(goals[i][j]).sub(n.previousGoal).divideScalar(Math.max(dt,1e-5)).clampLength(0,12);
    delta.copy(n.goal).sub(n.p).multiplyScalar(this.def.stiffness)
     .addScaledVector(targetVelocity,this.def.damping*.90).addScaledVector(n.v,-this.def.damping*(1+water))
     .addScaledVector(wind,1.7*(1-water*.8)).addScaledVector(down,.40*(1-water));
    n.v.addScaledVector(delta,h).clampLength(0,3);n.p.addScaledVector(n.v,h);
   }));
   for(let iteration=0;iteration<4;iteration++){
    for(const ch of this.nodes)for(let j=1;j<ch.length;j++){
     const a=ch[j-1],b=ch[j];if(b.pinned)continue;
     const length=a.goal.distanceTo(b.goal);delta.copy(b.p).sub(a.p);const d=delta.length();
     if(d>1e-7){delta.multiplyScalar((d-length)/d);if(a.pinned)b.p.sub(delta);else{a.p.addScaledVector(delta,.5);b.p.addScaledVector(delta,-.5);}}
    }
    if(this.def.coupled)for(let i=1;i<this.nodes.length;i++)for(let j=1;j<this.nodes[i].length;j++){
     const a=this.nodes[i-1][j],b=this.nodes[i][j],limit=a.goal.distanceTo(b.goal)*1.025;
     delta.copy(b.p).sub(a.p);const d=delta.length();if(d>limit){delta.multiplyScalar((d-limit)/d*.5);a.p.add(delta);b.p.sub(delta);}
    }
    for(const ch of this.nodes)for(let j=0;j<ch.length;j++){
     const n=ch[j];if(n.pinned)continue;
     // Limit hair travel during sprint/flight acceleration and leave collision corrections inelastic.
     delta.copy(n.p).sub(n.goal);const limit=this.def.maxOffset*Math.min(1,j/(ch.length-1)+.20);
     if(delta.length()>limit){n.p.copy(n.goal).add(delta.setLength(limit));n.v.multiplyScalar(.65);}
     caps.forEach((c,k)=>{
      const d=closest(n.p,c.a,c.b,q),r=n.radii[k]??c.radius;
      if(d<r&&r>.005){delta.copy(n.p).sub(q);if(d<1e-7)delta.copy(n.goal).sub(q);if(delta.lengthSq()<1e-10)delta.set(0,0,-1);n.p.copy(q).add(delta.setLength(r));
       const into=n.v.dot(delta.normalize());if(into<0)n.v.addScaledVector(delta,-into);
      }
     });
    }
   }
  }
  this.nodes.forEach((ch,i)=>ch.forEach((n,j)=>n.previousGoal.copy(goals[i][j])));
 }
}

/** Bake accessory weights once per cached template / skeleton size. Four influences maximum. */
const skinCache=new WeakMap<THREE.BufferGeometry,Map<number,{joints:THREE.BufferAttribute;weights:THREE.BufferAttribute}>>();
function hairSkin(template:THREE.BufferGeometry,def:HairRigDef,base:number){
 let cache=skinCache.get(template);if(!cache){cache=new Map();skinCache.set(template,cache);}const existing=cache.get(base);if(existing)return existing;
 const positions=template.getAttribute('position'),sourceJ=template.getAttribute('skinIndex'),sourceW=template.getAttribute('skinWeight');
 const joints=new Uint16Array(positions.count*4),weights=new Float32Array(positions.count*4);
 for(let i=0;i<positions.count;i++)for(let k=0;k<4;k++){joints[i*4+k]=sourceJ.getComponent(i,k);weights[i*4+k]=sourceW.getComponent(i,k);}
 const offsets:number[]=[];let total=base;for(const c of def.chains){offsets.push(total);total+=c.points.length;}
 const chains=def.chains.map(c=>c.points.map(p=>new THREE.Vector3(...p)));
 const p=new THREE.Vector3(),q=new THREE.Vector3(),d=new THREE.Vector3();const index=template.index!;
 for(const section of def.sections)for(let at=section.start;at<section.start+section.count;at++){
  const vi=index.getX(at);p.fromBufferAttribute(positions,vi);
  // All duplicate render vertices at a welded point receive the same weights.
  // Keep the scalp fixed while the joined tail/braid branches move below their roots.
  if(def.region==='ponytail'){
   // The tapered end curves toward the neck. A fixed rear plane left its
   // front half attached to the body instead of following the tail bones.
   const rearBorder=p.y>=1.687?-.110:THREE.MathUtils.lerp(-.115,-.145,clamp((p.y-1.55)/.09,0,1));
   if(p.z>rearBorder)continue;
  }
  if(def.region==='braid'&&(p.y>1.59||p.z>-.08))continue;
  if(def.region==='twin-braids'&&(p.y>1.616||p.z>-.025||(Math.abs(p.x)<.055&&p.y>1.565)))continue;
  const eligible=def.region==='twin-braids'?[p.x<0?0:1]:section.chains;
  const candidates=eligible.map(ci=>{
   const chain=chains[ci];let best={distance:Infinity,ci,segment:0,t:0};
   for(let j=0;j<chain.length-1;j++){
    d.copy(chain[j+1]).sub(chain[j]);const t=clamp(q.copy(p).sub(chain[j]).dot(d)/d.lengthSq(),0,1);
    q.copy(chain[j]).addScaledVector(d,t);const distance=q.distanceTo(p);
    if(distance<best.distance)best={distance,ci,segment:j,t};
   }return best;
  }).sort((a,b)=>a.distance-b.distance).slice(0,2);
  const strengths=candidates.map(c=>1/(c.distance*c.distance+.0001));const sum=strengths.reduce((a,b)=>a+b,0);
  for(let k=0;k<4;k++){joints[vi*4+k]=0;weights[vi*4+k]=0;}
  candidates.forEach((c,k)=>{const w=strengths[k]/sum;joints[vi*4+k*2]=offsets[c.ci]+c.segment;joints[vi*4+k*2+1]=offsets[c.ci]+c.segment+1;weights[vi*4+k*2]=w*(1-c.t);weights[vi*4+k*2+1]=w*c.t;});
 }
 const result={joints:new THREE.BufferAttribute(joints,4),weights:new THREE.BufferAttribute(weights,4)};cache.set(base,result);return result;
}

export class HairMotion {
 readonly solver:HairSolver;
 readonly skeleton:THREE.Skeleton;
 private bones:THREE.Bone[][]=[];
 private locals:THREE.Vector3[][]=[];
 private restRotations:THREE.Quaternion[][]=[];
 private goals:THREE.Vector3[][]=[];
 private caps:{a:THREE.Bone;b:THREE.Bone;radius:number;pa:THREE.Vector3;pb:THREE.Vector3}[]=[];
 private frame:THREE.Object3D|null|undefined;
 private head:THREE.Bone;
 private worldToFrame=new THREE.Matrix4();
 private frameToWorld=new THREE.Matrix4();
 private headInverse=new THREE.Matrix4();
 private headInFrame=new THREE.Quaternion();
 private headFrame=new THREE.Matrix4();
 private groupRotation=new THREE.Quaternion();
 private scratchPosition=new THREE.Vector3();
 private scratchScale=new THREE.Vector3();
 private collisionShapes:HairCollision[]=[];
 private localSwing=new THREE.Quaternion();
 private swing=new THREE.Quaternion();
 private direction=new THREE.Vector3();
 private goalDirection=new THREE.Vector3();
 private down=new THREE.Vector3();
 constructor(readonly mesh:THREE.SkinnedMesh,template:THREE.BufferGeometry,readonly def:HairRigDef){
  const base=mesh.skeleton,headIndex=base.bones.findIndex(b=>b.name==='head');this.head=base.bones[headIndex];
  const all=[...base.bones],inverses=[...base.boneInverses];
  const toHead=new THREE.Matrix4().multiplyMatrices(base.boneInverses[headIndex],mesh.bindMatrix);
  def.chains.forEach((chain,ci)=>{
   const bones:THREE.Bone[]=[],locals:THREE.Vector3[]=[],rotations:THREE.Quaternion[]=[];
   chain.points.forEach((point,i)=>{
    const bind=new THREE.Matrix4().makeTranslation(...point),local=toHead.clone().multiply(bind),bone=new THREE.Bone();
    bone.name=`hair_${chain.name}_${i}`;local.decompose(bone.position,bone.quaternion,bone.scale);this.head.add(bone);
    bones.push(bone);locals.push(bone.position.clone());rotations.push(bone.quaternion.clone());all.push(bone);
    inverses.push(new THREE.Matrix4().multiplyMatrices(mesh.bindMatrix,bind).invert());
   });this.bones[ci]=bones;this.locals[ci]=locals;this.restRotations[ci]=rotations;this.goals[ci]=chain.points.map(()=>new THREE.Vector3());
  });
  this.skeleton=new THREE.Skeleton(all,inverses);mesh.bind(this.skeleton,mesh.bindMatrix);
  const skin=hairSkin(template,def,base.bones.length);mesh.geometry.setAttribute('skinIndex',skin.joints);mesh.geometry.setAttribute('skinWeight',skin.weights);
  this.solver=new HairSolver(def);
  for(const [a,b,r]of [['neck_01','head',.048],['spine_05','neck_01',.10],['neck_01','upperarm_l',.065],['neck_01','upperarm_r',.065]] as [string,string,number][]){
   const A=base.bones.find(x=>x.name===a),B=base.bones.find(x=>x.name===b);if(A&&B)this.caps.push({a:A,b:B,radius:r,pa:new THREE.Vector3(),pb:new THREE.Vector3()});
  }
  this.collisionShapes=this.caps.map(c=>({a:c.pa,b:c.pb,radius:c.radius}));
 }
 reset():void{this.solver.reset();}
 step(dt:number,group:THREE.Object3D,frame:THREE.Object3D|null,wind:THREE.Vector3,water:number,enabled=true):void{
  if(this.frame!==frame){this.frame=frame;this.reset();}frame?.updateWorldMatrix(true,false);
  if(frame)this.frameToWorld.copy(frame.matrixWorld);else this.frameToWorld.identity();this.worldToFrame.copy(this.frameToWorld).invert();
  this.head.updateWorldMatrix(true,false);this.headInverse.copy(this.head.matrixWorld).invert();
  const headFrame=this.headFrame.multiplyMatrices(this.worldToFrame,this.head.matrixWorld);headFrame.decompose(this.scratchPosition,this.headInFrame,this.scratchScale);
  this.locals.forEach((ch,i)=>ch.forEach((p,j)=>this.goals[i][j].copy(p).applyMatrix4(headFrame)));
  this.caps.forEach(c=>{c.a.getWorldPosition(c.pa).applyMatrix4(this.worldToFrame);c.b.getWorldPosition(c.pb).applyMatrix4(this.worldToFrame);});
  this.down.set(0,-1,0).applyQuaternion(group.getWorldQuaternion(this.groupRotation)).transformDirection(this.worldToFrame);
  if(!enabled)this.reset();
  this.solver.step(enabled?dt:0,this.goals,this.collisionShapes,wind,this.down,water);
  this.bones.forEach((chain,ci)=>chain.forEach((bone,j)=>{
   const nodes=this.solver.nodes[ci],end=Math.min(j+1,nodes.length-1),start=j===end?j-1:j;
   bone.position.copy(nodes[j].p).applyMatrix4(this.frameToWorld).applyMatrix4(this.headInverse);
   this.direction.copy(nodes[end].p).sub(nodes[start].p).normalize();this.goalDirection.copy(this.goals[ci][end]).sub(this.goals[ci][start]).normalize();
   this.swing.setFromUnitVectors(this.goalDirection,this.direction);
   // Keep pinned root segments rigid. Swing-only deltas preserve the authored strand roll.
   if(j<defPinned(this.def,ci))this.swing.identity();
   this.localSwing.copy(this.headInFrame).invert().multiply(this.swing).multiply(this.headInFrame);
   bone.quaternion.copy(this.localSwing).multiply(this.restRotations[ci][j]);bone.updateWorldMatrix(false,false);
  }));
  this.skeleton.update();
 }
 dispose():void{for(const chain of this.bones)for(const b of chain)b.removeFromParent();this.skeleton.dispose();}
}
function defPinned(def:HairRigDef,chain:number):number{return def.chains[chain].pinned;}
